import React, { useState } from 'react';
import {
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useLocationContext } from '../../hooks/useLocationContext';
import { useListMessageRepliesQuery } from '../../store/api/localApi';
import { ThreadStatus } from '../../types';
import ThreadView from './ThreadView';

// ── MessageRepliesList ───────────────────────────────────────────────────────
//
// The conversations parents opened in reply to one staff message, shown on the
// sent-message page. Selecting a row swaps the list for the SHARED ThreadView,
// in-page rather than via a route: staff have no `/threads/:id` route, which is
// the same reason LocationThreadInbox opens threads inline. The endpoint is not
// paginated, so the full list renders at once.

function formatLastActivity(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  return then.toLocaleString('en-IE', {
    timeZone: 'Europe/Dublin',
    hour12: false,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const StatusChip: React.FC<{ threadId: string; status: ThreadStatus }> = ({ threadId, status }) => {
  const isOpen = status === ThreadStatus.OPEN;
  return (
    <span
      data-testid={`thread-status-${threadId}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium flex-shrink-0 ${
        isOpen ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'
      }`}
    >
      {isOpen ? <ClockIcon className="h-3 w-3" /> : <CheckCircleIcon className="h-3 w-3" />}
      {isOpen ? 'Waiting for a reply' : 'Answered'}
    </span>
  );
};

export interface MessageRepliesListProps {
  messageId: string;
  /** Org-wide sends reach every location, so each row names its location. */
  alwaysShowLocation?: boolean;
}

const MessageRepliesList: React.FC<MessageRepliesListProps> = ({
  messageId,
  alwaysShowLocation = false,
}) => {
  const { effectiveLocationId } = useLocationContext();
  const { data, isLoading, error } = useListMessageRepliesQuery({ messageId });
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);

  const replies = data?.replies ?? [];
  const openReply = replies.find((reply) => reply.threadId === openThreadId);

  if (openReply) {
    return (
      <section className="space-y-4" data-testid="sent-replies">
        <button
          type="button"
          onClick={() => setOpenThreadId(null)}
          data-testid="sent-replies-back"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to replies
        </button>
        {/* The list is swapped out, not stacked, so `thread-status-<id>` is on
            screen once — from the ThreadView — while a reply is open. */}
        <ThreadView threadId={openReply.threadId} title={`Reply from ${openReply.studentName}`} />
      </section>
    );
  }

  return (
    <section className="bg-white shadow rounded-lg overflow-hidden" data-testid="sent-replies">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-900">Replies</h2>
      </div>

      {isLoading ? (
        <p
          className="px-6 py-8 text-sm text-gray-500 text-center"
          data-testid="sent-replies-loading"
        >
          Loading replies…
        </p>
      ) : error ? (
        <div className="px-6 py-8 text-center" data-testid="sent-replies-error">
          <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-400" />
          <p className="mt-2 text-sm text-gray-500">
            Couldn&apos;t load the replies. Please try again later.
          </p>
        </div>
      ) : replies.length === 0 ? (
        <p className="px-6 py-8 text-sm text-gray-500 text-center" data-testid="sent-replies-empty">
          No families have replied yet.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {replies.map((reply) => {
            const showLocation = alwaysShowLocation || reply.locationId !== effectiveLocationId;
            return (
              <li key={reply.threadId}>
                <button
                  type="button"
                  onClick={() => setOpenThreadId(reply.threadId)}
                  data-testid={`sent-reply-${reply.threadId}`}
                  className="w-full flex items-start gap-3 px-6 py-4 text-left hover:bg-gray-50"
                >
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {reply.studentName}
                      {showLocation && (
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          {reply.locationName}
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 text-sm text-gray-700 truncate">
                      {reply.lastMessagePreview}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {formatLastActivity(reply.lastMessageAt)}
                    </p>
                  </div>
                  <StatusChip threadId={reply.threadId} status={reply.status} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default MessageRepliesList;
